import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, DialogActions, DialogContent,
  DialogTitle, IconButton, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TableSortLabel, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import EmptyState from '../../../shared/components/EmptyState';
import AppDialog from '../../../shared/components/AppDialog';
import { dataTableSx, tableActionCellSx } from '../../../shared/components/TableHeader';
import { formatHistoryDateTime } from '../../../shared/utils/historyDateTime';
import { EMPTY_VALUE, formatNumber } from '../../../shared/utils/number';
import {
  OperationOutcome, SourceResult, errorLabel, sourceLabel, statusLabel,
} from './EmissionFactorSyncDialog';
import {
  SYNC_HISTORY_SORT,
  SYNC_HISTORY_STATUS_ALL,
  filterAndSortSyncHistory,
  paginateSyncHistory,
  syncHistoryMetrics,
  syncHistorySources,
  syncHistoryTimestamp,
} from '../model/emissionFactorSyncHistoryModel';
import { isTerminalSyncStatus } from '../model/emissionFactorSyncRecovery';
import { syncStatusTone } from '../model/emissionFactorSyncPresentation';

const ROWS_PER_PAGE = 10;
const triggerLabel = (t, trigger) => t(
  'emissionFactor.sync.triggers.' + (trigger || 'MANUAL'),
  { defaultValue: t('emissionFactor.sync.triggers.UNKNOWN') },
);

function StatusChip({ status }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      label={statusLabel(t, status)}
      color={syncStatusTone(status)}
      sx={{ borderRadius: '6px', fontWeight: 700 }}
    />
  );
}

function SourceChips({ entry }) {
  const { t } = useTranslation();
  const sources = syncHistorySources(entry);
  if (!sources.length) return <Typography variant="caption">{EMPTY_VALUE}</Typography>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {sources.map((source) => (
        <Chip
          key={source.source_type}
          size="small"
          label={sourceLabel(t, source.source_type)}
          color={syncStatusTone(source.status)}
          variant="outlined"
          sx={{ borderRadius: '6px' }}
        />
      ))}
    </Stack>
  );
}

function ResultSummary({ entry }) {
  const { t } = useTranslation();
  const metrics = syncHistoryMetrics(entry);
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {t('emissionFactor.sync.history.sourcesCompleted', {
          completed: metrics.successfulSources,
          total: metrics.sourceCount,
        })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('emissionFactor.sync.history.publishedChanged', {
          published: formatNumber(metrics.publishedCount, { max: 0 }),
          changed: formatNumber(metrics.changedCount, { max: 0 }),
        })}
      </Typography>
    </Stack>
  );
}

function MetadataItem({ label, value, mono = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono ? 'monospace' : undefined,
          fontWeight: 700,
          overflowWrap: 'anywhere',
        }}
      >
        {value ?? EMPTY_VALUE}
      </Typography>
    </Box>
  );
}

const formattedTokenCount = (value) => (
  value === null ? EMPTY_VALUE : formatNumber(value, { max: 0 })
);

const formatDuration = (milliseconds, t) => {
  if (milliseconds === null) return EMPTY_VALUE;
  if (milliseconds < 1000) {
    return t('emissionFactor.sync.history.durationMilliseconds', {
      count: formatNumber(milliseconds, { max: 0 }),
    });
  }
  const totalSeconds = Math.round(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return t('emissionFactor.sync.history.durationHours', { hours, minutes });
  }
  if (minutes > 0) {
    return t('emissionFactor.sync.history.durationMinutes', { minutes, seconds });
  }
  return t('emissionFactor.sync.history.durationSeconds', { seconds });
};

function TokenUsageMetadata({ tokenUsage }) {
  const { t } = useTranslation();
  const hasFinalUsage = ['EXACT', 'PARTIAL'].includes(tokenUsage.availability);
  let total = EMPTY_VALUE;
  if (tokenUsage.availability === 'PENDING') {
    total = t('emissionFactor.sync.history.tokenUsagePending');
  } else if (tokenUsage.availability === 'EXACT') {
    total = t('emissionFactor.sync.history.tokenUsageTotal', {
      count: formattedTokenCount(tokenUsage.totalTokens),
    });
  } else if (tokenUsage.availability === 'PARTIAL') {
    total = t('emissionFactor.sync.history.tokenUsagePartial', {
      count: formattedTokenCount(tokenUsage.totalTokens),
    });
  } else {
    total = t('emissionFactor.sync.history.tokenUsageUnavailable');
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('emissionFactor.sync.history.tokens')}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {total}
      </Typography>
      {hasFinalUsage && (
        <Typography variant="caption" color="text.secondary">
          {t('emissionFactor.sync.history.tokenUsageBreakdown', {
            prompt: formattedTokenCount(tokenUsage.promptTokens),
            completion: formattedTokenCount(tokenUsage.completionTokens),
            cached: formattedTokenCount(tokenUsage.cachedTokens),
          })}
        </Typography>
      )}
    </Box>
  );
}

function SyncHistoryDetailDialog({
  open, entry, onClose, onExited,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const sources = syncHistorySources(entry);
  const metrics = syncHistoryMetrics(entry);
  const operationError = entry?.error_summary;
  const isTerminal = isTerminalSyncStatus(entry?.status);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      TransitionProps={{ onExited }}
      aria-labelledby="ef-sync-history-detail-title"
      sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
      PaperProps={{ sx: { maxHeight: { xs: '100%', sm: 'calc(100vh - 64px)' } } }}
    >
      <DialogTitle id="ef-sync-history-detail-title" sx={{ pr: 6 }}>
        {t('emissionFactor.sync.history.detailTitle')}
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {entry && (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <MetadataItem
                  label={t('emissionFactor.sync.history.column.date')}
                  value={formatHistoryDateTime(syncHistoryTimestamp(entry), i18n.language)}
                />
                <MetadataItem
                  label={t('emissionFactor.sync.history.column.year')}
                  value={entry.fiscal_year}
                />
                <MetadataItem
                  label={t('emissionFactor.sync.history.column.trigger')}
                  value={triggerLabel(t, entry.trigger)}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('emissionFactor.sync.history.column.status')}
                  </Typography>
                  <StatusChip status={entry.status} />
                </Box>
                <MetadataItem
                  label={t('emissionFactor.sync.history.duration')}
                  value={formatDuration(metrics.durationMs, t)}
                />
                <TokenUsageMetadata tokenUsage={metrics.tokenUsage} />
                {!isTerminal && (
                  <MetadataItem
                    label={t('emissionFactor.sync.history.stage')}
                    value={t(`emissionFactor.sync.history.stages.${entry.stage}`, {
                      defaultValue: t('emissionFactor.sync.history.stages.UNKNOWN'),
                    })}
                  />
                )}
              </Box>
            </Paper>

            {entry.reason && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <MetadataItem
                  label={t('emissionFactor.sync.history.reason')}
                  value={entry.reason}
                />
              </Paper>
            )}

            {operationError && (
              <Alert severity="error">
                {errorLabel(
                  typeof operationError === 'string'
                    ? operationError
                    : operationError.message || operationError.code,
                  typeof operationError === 'string' ? {} : operationError.params,
                )}
              </Alert>
            )}
            <OperationOutcome result={entry.summary || entry} />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('emissionFactor.sync.history.sourceResults')}
              </Typography>
              {sources.length ? (
                <Stack spacing={1.25}>
                  {sources.map((source) => (
                    <SourceResult
                      key={entry.operation_id + ':' + source.source_type}
                      source={source}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('emissionFactor.sync.history.noSourceResults')}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </AppDialog>
  );
}

export default function EmissionFactorSyncHistoryDialog({
  open, entries = [], loading = false, onClose,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [sortDirection, setSortDirection] = useState(SYNC_HISTORY_SORT.NEWEST);
  const [page, setPage] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSortDirection(SYNC_HISTORY_SORT.NEWEST);
    setPage(0);
    setDetailOpen(false);
    setSelectedEntry(null);
  }, [open]);

  const filteredEntries = useMemo(
    () => filterAndSortSyncHistory(entries, {
      status: SYNC_HISTORY_STATUS_ALL,
      direction: sortDirection,
    }),
    [entries, sortDirection],
  );
  const visibleEntries = useMemo(
    () => paginateSyncHistory(filteredEntries, page, ROWS_PER_PAGE),
    [filteredEntries, page],
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredEntries.length / ROWS_PER_PAGE) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredEntries.length, page]);

  const closeDetail = () => setDetailOpen(false);
  const resetDetailPayload = () => setSelectedEntry(null);
  const openDetail = (entry) => {
    setSelectedEntry(entry);
    setDetailOpen(true);
  };
  const resetTable = () => {
    setPage(0);
    setDetailOpen(false);
  };
  const toggleSort = () => {
    setSortDirection((current) => (
      current === SYNC_HISTORY_SORT.NEWEST
        ? SYNC_HISTORY_SORT.OLDEST
        : SYNC_HISTORY_SORT.NEWEST
    ));
    resetTable();
  };
  const handleClose = () => {
    setDetailOpen(false);
    onClose();
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
        PaperProps={{ sx: { maxHeight: { xs: '100%', sm: 'calc(100vh - 48px)' } } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t('emissionFactor.sync.historyTitle')}
          <IconButton
            onClick={handleClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', minHeight: 0, overflow: 'hidden', p: { xs: 2, sm: 2.5 } }}
        >
          {loading && entries.length === 0 ? (
            <Box sx={{ display: 'grid', flex: 1, minHeight: 220, placeItems: 'center' }}>
              <CircularProgress size={32} aria-label={t('emissionFactor.sync.history.loading')} />
            </Box>
          ) : entries.length === 0 ? (
            <Box sx={{ display: 'grid', flex: 1, minHeight: 220, placeItems: 'center' }}>
              <EmptyState
                title={t('emissionFactor.sync.historyEmpty')}
                description={t('emissionFactor.sync.historyEmptyHint')}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0, minWidth: 0, gap: 1.5 }}>
              {loading && <LinearProgress aria-label={t('emissionFactor.sync.history.loading')} />}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SwapVertRoundedIcon fontSize="small" />}
                    onClick={toggleSort}
                    sx={{ display: { xs: 'inline-flex', sm: 'none' }, whiteSpace: 'nowrap' }}
                  >
                    {t(sortDirection === SYNC_HISTORY_SORT.NEWEST
                      ? 'emissionFactor.sync.history.sortNewest'
                      : 'emissionFactor.sync.history.sortOldest')}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('emissionFactor.sync.history.resultCount', {
                    filtered: filteredEntries.length,
                    total: entries.length,
                  })}
                </Typography>
              </Stack>

              <Paper
                variant="outlined"
                sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
              >
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table
                    stickyHeader
                    size="small"
                    sx={(muiTheme) => ({
                       ...dataTableSx(muiTheme),
                       minWidth: { xs: 620, sm: 780, md: 980 },
                       '& .MuiTableHead-root .MuiTableCell-root': { textTransform: 'none' },
                    })}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 158 }}>
                          <TableSortLabel
                            active
                            direction={sortDirection}
                            onClick={toggleSort}
                          >
                            {t('emissionFactor.sync.history.column.date')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: 92 }}>
                          {t('emissionFactor.sync.history.column.year')}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 112 }}>
                          {t('emissionFactor.sync.history.column.trigger')}
                        </TableCell>
                        <TableCell sx={{ width: 154 }}>
                          {t('emissionFactor.sync.history.column.status')}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: 170 }}>
                          {t('emissionFactor.sync.history.column.sources')}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 190 }}>
                          {t('emissionFactor.sync.history.column.summary')}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 116 }}>
                          {t('emissionFactor.sync.history.column.actions')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleEntries.map((entry) => (
                        <TableRow
                          key={entry.operation_id}
                          hover
                          selected={selectedEntry?.operation_id === entry.operation_id}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <Typography variant="caption">
                              {formatHistoryDateTime(syncHistoryTimestamp(entry), i18n.language)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {entry.fiscal_year}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: 'block', sm: 'none' } }}
                            >
                              {triggerLabel(t, entry.trigger)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, verticalAlign: 'top' }}>
                            <Typography variant="caption">
                              {triggerLabel(t, entry.trigger)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <StatusChip status={entry.status} />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, verticalAlign: 'top' }}>
                            <SourceChips entry={entry} />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, verticalAlign: 'top' }}>
                            <ResultSummary entry={entry} />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...tableActionCellSx, verticalAlign: 'top' }}
                          >
                            <Tooltip title={t('emissionFactor.sync.history.viewDetails')}>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                                onClick={() => openDetail(entry)}
                                aria-label={t('emissionFactor.sync.history.viewDetailsFor', {
                                  id: entry.operation_id,
                                })}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {t('emissionFactor.sync.history.details')}
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {visibleEntries.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {t('emissionFactor.sync.history.noFilteredResults')}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredEntries.length}
                  page={page}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  onPageChange={(_event, nextPage) => {
                    setPage(nextPage);
                    setDetailOpen(false);
                  }}
                  sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    flex: '0 0 auto',
                    '& .MuiTablePagination-toolbar': {
                      minHeight: 52,
                      px: { xs: 1, sm: 2 },
                    },
                    '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
                    '& .MuiTablePagination-displayedRows': {
                      flex: { xs: 1, sm: 'initial' },
                      ml: { xs: 0 },
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiTablePagination-actions': { ml: { xs: 0.5, sm: 2.5 } },
                  }}
                />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleClose}>{t('common.close')}</Button>
        </DialogActions>
      </AppDialog>
      <SyncHistoryDetailDialog
        open={detailOpen}
        entry={selectedEntry}
        onClose={closeDetail}
        onExited={resetDetailPayload}
      />
    </>
  );
}
